import { useCallback } from 'react';

const isBlockedInlineStyle = (property: string, value: string): boolean => {
  if (property === 'background' || property === 'background-color' || property === 'z-index') {
    return true;
  }

  if (property === 'position' && (value === 'fixed' || value === 'sticky')) {
    return true;
  }

  return false;
};

const sanitizeInlineStyle = (element: HTMLElement): void => {
  const safeStyles: Array<[string, string]> = [];

  for (let i = 0; i < element.style.length; i++) {
    const property = element.style.item(i);

    if (!property) {
      continue;
    }

    const rawValue = element.style.getPropertyValue(property);

    const valueWithoutImportant = rawValue.split('!important').join('').trim();

    if (!valueWithoutImportant) {
      continue;
    }

    const normalizedValue = valueWithoutImportant.toLowerCase();
    if (isBlockedInlineStyle(property, normalizedValue)) {
      continue;
    }

    safeStyles.push([property, valueWithoutImportant]);
  }

  element.removeAttribute('style');
  safeStyles.forEach(([property, value]) => {
    element.style.setProperty(property, value);
  });

  if (safeStyles.length === 0) {
    element.removeAttribute('style');
  }
};

const applyTargetBlankToLinks = (container: HTMLElement): void => {
  container.querySelectorAll('a').forEach(link => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });
};

const applyNoReferrerToImages = (container: HTMLElement): void => {
  container.querySelectorAll('img').forEach(image => {
    image.setAttribute('referrerpolicy', 'no-referrer');
  });
};

const removeStyleTags = (container: HTMLElement): void => {
  container.querySelectorAll('style').forEach(el => el.remove());
};

// Some emails arrive as plain text with no HTML markup at all (e.g. auto-generated
// return request forms), so the browser's default whitespace collapsing swallows every
// line break and indent. Preserve them when no tags were present in the source.
const preserveLineBreaksForPlainTextContent = (container: HTMLElement): void => {
  if (container.children.length > 0) return;

  const text = container.textContent ?? '';
  if (!text.trim()) return;

  const wrapper = document.createElement('span');
  wrapper.style.whiteSpace = 'pre-wrap';
  wrapper.textContent = text;

  container.replaceChildren(wrapper);
};

export const processEmailHtml = (html: string): string => {
  const container = document.createElement('div');
  container.innerHTML = html;

  preserveLineBreaksForPlainTextContent(container);

  removeStyleTags(container);

  container.querySelectorAll<HTMLElement>('[style]').forEach(element => {
    sanitizeInlineStyle(element);
  });

  applyNoReferrerToImages(container);

  applyTargetBlankToLinks(container);

  return container.innerHTML;
};

export const useProcessedEmailHtml = (): ((html: string) => string) => {
  return useCallback((html: string) => processEmailHtml(html), []);
};
